import {
  excludeResultsOnOrAfterDay,
  nextResultHistoryCursor,
  type ResultHistoryCursor,
} from '../../core/lottery/resultHistory';
import { resolveResultsFeedForHouse } from '../../core/lottery/mergePlatformDrawResults';
import type { ResultLike } from '../../core/rules/officialDrawResults';
import type { PlatformLotteryDraw } from '../lottery/platformLotteryDrawTypes';

export type ResultHistoryPageIo = {
  fetchPlatformLotteryDrawsPage: (opts: {
    source: 'florida' | 'newyork';
    limitCount: number;
    cursor?: { dayKey: string; window: 'day' | 'night' } | null;
  }) => Promise<{
    rows: PlatformLotteryDraw[];
    hasMore: boolean;
    cursor?: { dayKey: string; window: 'day' | 'night' } | null;
  }>;
  fetchHouseResultsPage: (opts: {
    houseId: string;
    limitCount: number;
    cursor?: ResultHistoryCursor | null;
  }) => Promise<{ rows: ResultLike[]; hasMore: boolean }>;
};

const HOUSE_ARCHIVE_SKIP_MAX = 8;

function cursorFromRows(
  preferred: readonly ResultLike[],
  fallback: readonly ResultLike[],
): ResultHistoryCursor | null {
  return nextResultHistoryCursor(preferred) ?? nextResultHistoryCursor(fallback);
}

function cursorFromPlatformDraws(rows: readonly PlatformLotteryDraw[]): ResultHistoryCursor | null {
  return nextResultHistoryCursor(rows.map((row) => ({
    dayKey: row.dayKey,
    window: row.window,
  })));
}

async function fetchHouseArchivePage(
  opts: {
    houseId: string;
    pageSize: number;
    cursor: ResultHistoryCursor | null;
    weekFloorDayKey?: string;
  },
  fetchHouse: ResultHistoryPageIo['fetchHouseResultsPage'],
): Promise<{ rows: ResultLike[]; hasMore: boolean; cursor: ResultHistoryCursor | null }> {
  const weekFloor = String(opts.weekFloorDayKey ?? '').trim();
  let cursor = opts.cursor;
  let hops = 0;
  let lastRaw: ResultLike[] = [];
  let lastHasMore = false;
  let rows: ResultLike[] = [];
  do {
    const page = await fetchHouse({
      houseId: opts.houseId,
      limitCount: opts.pageSize,
      cursor,
    });
    lastRaw = page.rows;
    lastHasMore = page.hasMore;
    rows = weekFloor ? excludeResultsOnOrAfterDay(page.rows, weekFloor) : page.rows;
    cursor = nextResultHistoryCursor(page.rows);
    hops += 1;
  } while (rows.length === 0 && lastHasMore && cursor && hops < HOUSE_ARCHIVE_SKIP_MAX);
  return {
    rows,
    hasMore: lastHasMore,
    cursor: cursorFromRows(rows, lastRaw),
  };
}

export async function fetchResultHistoryPage(
  opts: {
    platform: boolean;
    source: string | null | undefined;
    houseId: string;
    pageSize: number;
    cursor: ResultHistoryCursor | null;
    weekFloorDayKey?: string;
  },
  io?: ResultHistoryPageIo,
): Promise<{ rows: ResultLike[]; hasMore: boolean; cursor: ResultHistoryCursor | null }> {
  if (opts.platform && (opts.source === 'florida' || opts.source === 'newyork')) {
    const fetchPlatform = io?.fetchPlatformLotteryDrawsPage
      ?? (await import('../lottery/subscribePlatformLotteryDraws')).fetchPlatformLotteryDrawsPage;
    const page = await fetchPlatform({
      source: opts.source,
      limitCount: opts.pageSize,
      cursor: opts.cursor
        ? { dayKey: opts.cursor.dayKey, window: opts.cursor.window }
        : null,
    });
    const rows = resolveResultsFeedForHouse([], page.rows, opts.source);
    return {
      rows,
      hasMore: page.hasMore,
      cursor: cursorFromRows(rows, [])
        ?? cursorFromPlatformDraws(page.rows)
        ?? page.cursor
        ?? null,
    };
  }

  const fetchHouse = io?.fetchHouseResultsPage
    ?? (await import('../../adapters/data/firebaseResultHistory')).fetchHouseResultsPage;
  return fetchHouseArchivePage({
    houseId: opts.houseId,
    pageSize: opts.pageSize,
    cursor: opts.cursor,
    weekFloorDayKey: opts.weekFloorDayKey
      ?? (opts.cursor?.publishedAtMs == null ? opts.cursor?.dayKey : undefined),
  }, fetchHouse);
}
